import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/currency';
import { Smartphone, FileText, Wrench, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const TenantDashboard = () => {
    const [data, setData] = useState(null);
    const [paying, setPaying] = useState(false);
    const [phone, setPhone] = useState('');
    const [showPay, setShowPay] = useState(false);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

    const load = async () => {
        try {
            const r = await axios.get('/api/portal/me');
            setData(r.data);
            if (r.data.next_invoice?.id) setSelectedInvoiceId(r.data.next_invoice.id);
        } catch { toast.error('Failed to load'); }
    };
    useEffect(() => { load(); }, []);

    const startPay = async (amount) => {
        setPaying(true);
        try {
            const { data: r } = await axios.post('/api/portal/pay', {
                amount,
                invoice_id: selectedInvoiceId || undefined,
                phone: phone || undefined,
            });
            toast.success(r.customer_message || 'Check your phone for the M-Pesa prompt');
            setShowPay(false);
            const iv = setInterval(async () => {
                try {
                    const { data: p } = await axios.get(`/api/portal/pay/${r.intent_id}`);
                    if (p.intent.status === 'success') { clearInterval(iv); toast.success('Payment received!'); load(); }
                    if (p.intent.status === 'failed')  { clearInterval(iv); toast.error(p.intent.result_desc || 'Failed'); }
                } catch {}
            }, 4000);
            setTimeout(() => clearInterval(iv), 120000);
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
        finally { setPaying(false); }
    };

    if (!data) return <div>Loading…</div>;
    if (!data.tenant) return (
        <div className="max-w-3xl mx-auto bg-white rounded-xl border p-8 text-center">
            <p>You don't have an active lease on file. Please contact your landlord.</p>
        </div>
    );

    const s = data.summary || {};
    const n = data.next_invoice;

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <div>
                <h1 className="text-2xl font-bold">Welcome</h1>
                <p className="text-gray-600">{data.tenant.property_name} · {data.tenant.address}</p>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
                <Card label="Outstanding balance" value={formatCurrency(s.outstanding || 0)} accent={s.outstanding > 0 ? 'text-red-600':'text-green-700'} />
                <Card label="Paid to date" value={formatCurrency(s.paid || 0)} />
                <Card label="Total billed" value={formatCurrency(s.billed || 0)} />
            </div>

            {n && (
                <div className="bg-primary-50 border border-primary-200 rounded-xl p-5">
                    <div className="flex flex-wrap justify-between gap-3 items-center">
                        <div>
                            <div className="text-sm text-primary-700">Next payment due</div>
                            <div className="text-xl font-semibold">{formatCurrency(n.balance)} · {new Date(n.due_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                            <div className="text-xs text-gray-500 font-mono">{n.invoice_number}</div>
                        </div>
                        <button onClick={() => setShowPay(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg">
                            <Smartphone className="h-4 w-4" /> Pay with M-Pesa
                        </button>
                    </div>
                </div>
            )}

            {(data.invoices || []).length > 1 && (
                <div className="bg-white rounded-xl border p-4">
                    <div className="text-sm font-medium text-gray-700 mb-2">Outstanding invoices</div>
                    <div className="grid gap-2 sm:grid-cols-2">
                        {(data.invoices || []).map(inv => (
                            <button
                                key={inv.id}
                                type="button"
                                onClick={() => setSelectedInvoiceId(inv.id)}
                                className={`text-left rounded-lg border p-3 ${selectedInvoiceId === inv.id ? 'border-primary-600 bg-primary-50' : 'border-gray-200 hover:border-gray-300'}`}
                            >
                                <div className="flex justify-between gap-3">
                                    <span className="font-mono text-xs">{inv.invoice_number}</span>
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 capitalize">{inv.status}</span>
                                </div>
                                <div className="mt-2 text-sm text-gray-600">Due {new Date(inv.due_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                                <div className="mt-1 font-semibold">{formatCurrency(inv.balance_due || inv.total_amount)}</div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className="grid md:grid-cols-3 gap-4">
                <QuickLink to="/portal/statement" icon={FileText} label="My statement" />
                <QuickLink to="/portal/maintenance" icon={Wrench} label="Maintenance" />
                <QuickLink to="/portal/lease" icon={FileText} label="My lease" />
            </div>

            {showPay && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-3">
                        <h2 className="font-semibold">Pay with M-Pesa</h2>
                        <p className="text-sm text-gray-600">You'll get a PIN prompt on your phone.</p>
                        {data.invoices && data.invoices.length > 0 && (
                            <select value={selectedInvoiceId || ''} onChange={e => setSelectedInvoiceId(e.target.value || null)} className="w-full border rounded-lg px-3 py-2">
                                <option value="">Pay outstanding balance</option>
                                {data.invoices.map(inv => (
                                    <option key={inv.id} value={inv.id}>{inv.invoice_number} · {formatCurrency(inv.balance_due || inv.total_amount)}</option>
                                ))}
                            </select>
                        )}
                        <input placeholder="Phone (e.g. 0712345678)" className="w-full border rounded-lg px-3 py-2" value={phone} onChange={e => setPhone(e.target.value)} />
                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={() => setShowPay(false)} className="px-4 py-2 border rounded-lg">Cancel</button>
                            <button
                                disabled={paying}
                                onClick={() => startPay(selectedInvoiceId ? Number((data.invoices.find(i => i.id === Number(selectedInvoiceId))?.balance_due || 0)) : Math.max(1, Math.round(s.outstanding)))}
                                className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50"
                            >
                                {paying ? 'Sending…' : `Pay ${formatCurrency(selectedInvoiceId ? Number(data.invoices.find(i => i.id === Number(selectedInvoiceId))?.balance_due || 0) : s.outstanding)}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const Card = ({ label, value, accent }) => (
    <div className="bg-white rounded-xl border p-5">
        <div className="text-sm text-gray-500">{label}</div>
        <div className={`text-2xl font-bold mt-1 ${accent||''}`}>{value}</div>
    </div>
);
const QuickLink = ({ to, icon: Icon, label }) => (
    <Link to={to} className="bg-white rounded-xl border p-4 hover:shadow-md transition flex items-center justify-between">
        <div className="flex items-center gap-3"><Icon className="h-5 w-5 text-primary-600" /><span className="font-medium">{label}</span></div>
        <ChevronRight className="h-4 w-4 text-gray-400" />
    </Link>
);

export default TenantDashboard;
